import {
  useRpiCalculation,
  formatLayerName,
  MatchType,
} from "./useRpiCalculation";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  AutoFixHigh as AutoIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
} from "@mui/material";
import * as numbers from "common/numbers";
import { useDispatch, useSelector } from "react-redux";
import * as programPricingSlice from "store/programPricing";
import * as stateSelectors from "store/selectors/stateSelectors";

/**
 * Gets a chip label and color for the match type
 */
const getMatchTypeChip = (matchType) => {
  switch (matchType) {
    case MatchType.EXACT:
      return { label: "Exact Match", color: "success" };
    case MatchType.CLOSE:
      return { label: "Close Match", color: "info" };
    case MatchType.BOUND_FALLBACK:
      return { label: "Bound Layer", color: "warning" };
    case MatchType.MANUAL:
      return { label: "Manual", color: "secondary" };
    default:
      return { label: "None", color: "default" };
  }
};

/**
 * Dialog component for selecting which prior layer to use for RPI calculation
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog closes
 * @param {string} props.layerId - Current layer ID
 * @param {string} props.currentPriorLayerId - Currently selected prior layer ID
 * @param {string} props.currentMatchType - Current match type
 */
const RpiPriorLayerSelector = ({
  open,
  onClose,
  layerId,
  currentPriorLayerId,
  currentMatchType,
}) => {
  const dispatch = useDispatch();
  const { priorLayers } = useRpiCalculation();
  const priorState = useSelector(stateSelectors.selectPriorState);

  /**
   * Gets the combined ratio for a prior layer
   */
  const getPriorLayerCombinedRatio = (priorLayer) => {
    if (!priorLayer || !priorState) {
      return null;
    }
    const priorAnalyticsLayers =
      priorState?.analytics?.programPricing?.values?.layers || {};
    return priorAnalyticsLayers[priorLayer.id]?.GGLRCombinedRatioFOT || null;
  };

  const handleSelectPriorLayer = (priorLayerId) => {
    // Use updatePathValue for proper nested update
    dispatch(
      programPricingSlice.updatePathValue({
        path: ["layers", layerId, "selectedPriorLayerIdForRpi"],
        value: priorLayerId,
      })
    );
    onClose();
  };

  const handleResetToAuto = () => {
    // Set to null to reset to auto-matching
    dispatch(
      programPricingSlice.updatePathValue({
        path: ["layers", layerId, "selectedPriorLayerIdForRpi"],
        value: null,
      })
    );
    onClose();
  };

  const matchChip = getMatchTypeChip(currentMatchType);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant={"h6"}>{"Select Prior Layer for RPI"}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant={"body2"} color={"text.secondary"}>
            {"Choose which prior layer to compare against for RPI calculation."}
          </Typography>
        </Box>

        <Box sx={{ mb: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}>
          <Typography variant={"caption"} color={"text.secondary"}>
            {"Current Selection:"}
          </Typography>
          {currentPriorLayerId &&
            (() => {
              const currentPriorLayer = priorLayers.find(
                (l) => l.id === currentPriorLayerId
              );
              const combinedRatio = currentPriorLayer
                ? getPriorLayerCombinedRatio(currentPriorLayer)
                : null;
              const hasCombinedRatio =
                combinedRatio != null && combinedRatio !== 0;

              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant={"body2"} sx={{ fontWeight: "bold" }}>
                      {formatLayerName(currentPriorLayer)}
                    </Typography>
                    <Chip
                      label={matchChip.label}
                      color={matchChip.color}
                      size={"small"}
                    />
                  </Box>
                  {hasCombinedRatio ? (
                    <Typography
                      variant={"caption"}
                      color={"text.secondary"}
                      sx={{ mt: 0.5 }}
                    >
                      {`Combined Ratio: ${numbers.percentage(1)(
                        combinedRatio
                      )}`}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <WarningIcon
                        sx={{ fontSize: 14, color: "warning.main" }}
                      />
                      <Typography variant={"caption"} color={"warning.main"}>
                        {"No combined ratio data"}
                      </Typography>
                    </Box>
                  )}
                </>
              );
            })()}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant={"subtitle2"} sx={{ mb: 1 }}>
          {"Available Prior Layers:"}
        </Typography>

        <List sx={{ maxHeight: 300, overflow: "auto" }}>
          {priorLayers.map((priorLayer) => {
            const isSelected = priorLayer.id === currentPriorLayerId;
            const isBound = priorLayer.status === "BOUND";
            const combinedRatio = getPriorLayerCombinedRatio(priorLayer);
            const hasCombinedRatio =
              combinedRatio != null && combinedRatio !== 0;

            return (
              <ListItem key={priorLayer.id} disablePadding>
                <ListItemButton
                  onClick={() => handleSelectPriorLayer(priorLayer.id)}
                  selected={isSelected}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {isSelected && <CheckIcon color={"primary"} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant={"body2"}>
                          {formatLayerName(priorLayer)}
                        </Typography>
                        {isBound && (
                          <Chip
                            label={"Bound"}
                            size={"small"}
                            color={"success"}
                            variant={"outlined"}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        {priorLayer.status && !isBound && (
                          <Typography
                            variant={"caption"}
                            color={"text.secondary"}
                          >
                            {`Status: ${priorLayer.status}`}
                          </Typography>
                        )}
                        {hasCombinedRatio ? (
                          <Typography
                            variant={"caption"}
                            color={"text.secondary"}
                          >
                            {`Combined Ratio: ${numbers.percentage(1)(
                              combinedRatio
                            )}`}
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <WarningIcon
                              sx={{ fontSize: 14, color: "warning.main" }}
                            />
                            <Typography
                              variant={"caption"}
                              color={"warning.main"}
                            >
                              {"No combined ratio data"}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {priorLayers.length === 0 && (
          <Typography variant={"body2"} color={"text.secondary"} sx={{ py: 2 }}>
            {"No prior layers available."}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {currentMatchType === MatchType.MANUAL && (
          <Button
            onClick={handleResetToAuto}
            startIcon={<AutoIcon />}
            color={"secondary"}
          >
            {"Reset to Auto-Match"}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          {"Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RpiPriorLayerSelector;
